
interface Point {
    x: number;
    y: number;
}

interface Intersection {
    point: Point;
    segment1Index: number;
    segment2Index: number;
}

/**
 * To process the polygon lines
 */
export class PolygonHandlerService {
    // Check if two line segments intersect
    private doSegmentsIntersect(
        p1: Point, p2: Point,
        p3: Point, p4: Point
    ): Point | null {
        const denominator = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
        
        if (denominator === 0) return null;
        
        const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denominator;
        const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denominator;
        
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null;
        
        return {
            x: p1.x + ua * (p2.x - p1.x),
            y: p1.y + ua * (p2.y - p1.y)
        };
    }

    // Find all intersections in the polygon
    private findIntersections(points: Point[]): Intersection[] {
        const intersections: Intersection[] = [];
        
        for (let i = 0; i < points.length - 1; i++) {
            for (let j = i + 2; j < points.length - 1; j++) {
                const intersection = this.doSegmentsIntersect(
                    points[i], points[i + 1],
                    points[j], points[j + 1]
                );
                
                if (intersection) {
                    intersections.push({
                        point: intersection,
                        segment1Index: i,
                        segment2Index: j
                    });
                }
            }
        }
        
        return intersections;
    }

    // Calculate polygon area
    private calculatePolygonArea(points: Point[]): number {
        let area = 0;
        for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += points[i].x * points[j].y;
            area -= points[j].x * points[i].y;
        }
        return Math.abs(area) / 2;
    }

    // Find the largest valid loop
    private findLargestLoop(points: Point[], intersections: Intersection[]): Point[] {
        if (intersections.length === 0) return points;

        const allLoops: Point[][] = [];
        const visited = new Set<number>();

        // Helper function to traverse and form loops
        const traverseLoop = (startIdx: number, currentLoop: Point[]) => {
            if (visited.has(startIdx)) {
                if (currentLoop.length >= 3) {
                    allLoops.push([...currentLoop]);
                }
                return;
            }

            visited.add(startIdx);
            currentLoop.push(points[startIdx]);

            // Find next possible points (including intersections)
            const nextPoints = intersections
                .filter(int => int.segment1Index === startIdx || int.segment2Index === startIdx)
                .map(int => ({
                    point: int.point,
                    nextIndex: int.segment1Index === startIdx ? int.segment2Index : int.segment1Index
                }));

            // Add regular next point
            if (startIdx < points.length - 1) {
                nextPoints.push({
                    point: points[startIdx + 1],
                    nextIndex: startIdx + 1
                });
            }

            for (const { nextIndex } of nextPoints) {
                traverseLoop(nextIndex, [...currentLoop]);
            }
        };

        // Start from each point to find all possible loops
        for (let i = 0; i < points.length; i++) {
            traverseLoop(i, []);
        }

        // Find the largest valid loop
        let largestArea = 0;
        let largestLoop: Point[] = points;

        for (const loop of allLoops) {
            const area = this.calculatePolygonArea(loop);
            if (area > largestArea) {
                largestArea = area;
                largestLoop = loop;
            }
        }

        return largestLoop;
    }

    // Main function to process the polygon
    public processPolygon(points: Point[]): Point[] {
        const intersections = this.findIntersections(points);
        return this.findLargestLoop(points, intersections);
    }
}